/**
 * @file AuthContext.jsx
 *
 * Authentication context and provider. Stores the current user object,
 * handles session restoration via httpOnly cookie on mount, and exposes
 * login success / logout helpers to the rest of the app.
 *
 * Auth tokens are stored in httpOnly cookies (set by the backend).
 * The frontend never reads or writes tokens directly.
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { authService } from "../services/auth";

/** @type {React.Context<Object|null>} */
const AuthContext = createContext(null);

/**
 * Provides authentication state to the component tree.
 * Restores the session from httpOnly cookies on mount by calling GET /auth/me.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @returns {React.ReactElement}
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing session on mount (httpOnly cookies sent automatically)
  useEffect(() => {
    const initAuth = async () => {
      try {
        const response = await authService.getMe();
        setUser(response.data);
      } catch {
        // getMe failed — try explicit refresh then retry
        try {
          await authService.refreshToken();
          const response = await authService.getMe();
          setUser(response.data);
        } catch {
          // No valid session — stay logged out
        }
      }
      setIsLoading(false);
    };
    initAuth();
  }, []);

  /**
   * Sets the user in state after a successful authentication.
   * Tokens are in httpOnly cookies (set by the backend response).
   * Extracts onboardingComplete/isEmailVerified from JWT in the response
   * body so they're available immediately.
   *
   * @param {Object} data - Auth response containing { user, accessToken, refreshToken }
   */
  const handleAuthSuccess = useCallback((data) => {
    // Tokens are in httpOnly cookies — don't store in localStorage
    // Parse JWT from response body to extract payload fields
    try {
      const payload = JSON.parse(atob(data.accessToken.split(".")[1]));
      if (payload.onboardingComplete !== undefined) data.user.onboardingComplete = payload.onboardingComplete;
      if (payload.isEmailVerified !== undefined) data.user.isEmailVerified = payload.isEmailVerified;
      if (payload.termsAccepted !== undefined) data.user.termsAccepted = payload.termsAccepted;
    } catch {}
    setUser(data.user);
  }, []);

  /** Calls logout API (which clears httpOnly cookies) and resets user state. */
  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch {
      // Ignore errors on logout
    }
    setUser(null);
  }, []);

  const value = {
    user,
    isLoading,
    isAuthenticated: !!user,
    handleAuthSuccess,
    logout,
    setUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Convenience hook to consume the AuthContext.
 * Must be used within an AuthProvider.
 *
 * @returns {{user: Object|null, isLoading: boolean, isAuthenticated: boolean, handleAuthSuccess: Function, logout: Function, setUser: Function}}
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default AuthContext;
